import { resolveTitleLabel } from '../profile-assets.js';

/**
 * The player's selected profile title (APP_SPEC §13.2) — a small accolade
 * under the identity block, part of profile customization rather than a
 * heading. When the user has no title selected (or the stored id is unknown)
 * this renders nothing at all: no "None", no empty placeholder row.
 *
 * Presented as an engraved line under a ruled diamond ornament rather than as
 * a bordered chip: a bordered pill reads as an interactive control, and the
 * hero already carries enough boxes in the Overall plaques beside it.
 *
 * The line is set in Alegreya's *drawn* italic — a literary, bookish accolade
 * — rather than as letterspaced uppercase, which read as a UI eyebrow label
 * sitting under the name instead of as part of the illustrated plate. It gets
 * negative tracking rather than the positive tracking a label would take, and
 * no `text-transform` at all: the words are exactly the selected title's
 * catalog label, in the catalog's own casing (`profile-assets.ts`), so nothing
 * here mutates or re-cases stored data.
 */
export interface SelectedProfileTitleProps {
  titleId: string | null;
}

export function SelectedProfileTitle({ titleId }: SelectedProfileTitleProps) {
  const label = resolveTitleLabel(titleId);
  if (!label) return null;

  return (
    <div className="mt-3 max-w-[18rem]">
      <span aria-hidden="true" className="flex items-center gap-2">
        <span className="h-px flex-1 bg-gradient-to-l from-border to-transparent" />
        <span className="h-[6px] w-[6px] shrink-0 rotate-45 border border-brand bg-brand/40" />
        <span className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
      </span>
      <p className="mt-1.5 text-center font-profile text-[1.3rem] font-medium italic leading-snug tracking-[-0.005em] text-textPrimary/90 tablet:text-[1.4rem]">
        {label}
      </p>
    </div>
  );
}
